import { Client } from "../client/typesDefinition";
import { Invoice, InvoicesMenuItem } from "../invoice/typesDefinition";
import { Order, RecurringOrder } from "../order/typesDefinition";
import { PaginatedResponse, Entity, PaginatedResult } from "../typesDefinition";
import { User } from "@/helpers/user/typesDefinition";

export interface ProjectRole extends Entity {
  name: string;
  hourlyRate: number;
  project: Project;
}

export interface TeamMember {
  user: User;
  projectRole: ProjectRole;
  active: boolean;
}

export interface Budget extends Entity {
  amount: number;
  approvedAt: Date;
}

export interface File extends Entity {
  url: string;
  name: string;
}

export interface Project extends Entity {
  name: string;
  client: Client;
  defaultPaymentOption: string;
  projectRoles: ProjectRole[];
  orders: PaginatedResult<Order>;
  nextOrderNumber: number;
  nextRecurringOrderNumber: number;
  invoicesMenu: InvoicesMenuItem[];
  budget?: Budget;
  previouslyApprovedBudget?: Budget;
  totalBilled: number;
  totalBilledNet: number;
  totalNotBilledOrdered: number;
  totalPaid: number;
  totalPaidNet: number;
  totalApprovedOrderedAmount: number;
  totalOrderedAmount: number;
  performedAmount: number;
  description?: string;
  lateApprovalOrdersCount: number;
  latePaymentInvoicesCount: number;
  latePaymentBalance: number;
  latePaymentDays: number;
  budgetWarning?: boolean;
  active: boolean;
  color?: string;
  internal: boolean;
  architectureImage?: File;
  withHourlyRate: boolean;
  isCowork: boolean;
  needsBudgetApproval: boolean;
  budgetTracking?: BudgetTracking;
  plannedTimeTracking?: PlannedTimeTracking;
}

export interface ProjectResponse {
  project: Project;
}

export interface ProjectRecurringOrdersResponse {
  project: {
    id: string;
    client: { id: string };
    recurringOrders: RecurringOrder[];
  };
}

export interface ProjectPaginatedResponse extends PaginatedResponse<Project> {
  projects: PaginatedResult<Project>;
}

export interface Theme extends Entity {
  name: string;
  project: Partial<Project> & { id: string; name: string };
  isFavoriteTheme: boolean;
}

export interface LoggedHours {
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
  project: {
    id: string;
  };
  projectRole?: ProjectRole;
  startDate: string;
  endDate: string;
  loggedHours: number;
  plannedHours: number;
}

export interface CurrentOrder {
  id: string;
  title: string;
  price: number;
  performedAmount: number;
  startDate: string;
  endDate: string;
  invoiced: boolean;
}

export enum PlannedTimeFrequency {
  NO_REPEAT = "no_repeat",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  EVERY_2_WEEKS = "every_2_weeks",
  EVERY_3_WEEKS = "every_3_weeks",
  EVERY_6_WEEKS = "every_6_weeks",
  EVERY_3_MONTHS = "every_3_months",
  EVERY_6_MONTHS = "every_6_months",
  YEARLY = "yearly",
}

export enum LeaveType {
  PAID_LEAVE = "paid_leave",
  SICK_LEAVE = "sick_leave",
}
export interface PlannedTime {
  id: string;
  user: User;
  startDate: Date;
  endDate: Date;
  durationPerDay: number;
  frequency: PlannedTimeFrequency;
  repeatEndDate?: Date;
  active: boolean;
  projectRole?: ProjectRole;
  project: Project;
}

export interface Holiday {
  id: string;
  date: Date;
  name?: string;
}

export interface TimeOff {
  id: string;
  user: User;
  durationPerDay: number;
  frequency: PlannedTimeFrequency;
  startDate: Date;
  endDate: Date;
  repeatEndDate?: Date;
  active: boolean;
  type?: LeaveType;
}

export interface InvoicedAmount {
  gross: number;
  net: number;
  paid: number;
}

export interface WorkingPeriod {
  startDate: string;
  endDate: string;
  performedAmount: number;
  expenseAmount: number;
  orderedAmount: number;
  invoicedAmount: InvoicedAmount;
  plannedTimes?: PlannedTime[];
  holidays?: Holiday[];
  timeOffs?: TimeOff[];
  invoices: Invoice[];
  orders: Order[];
}

export interface EmployeeBudgetAllocation {
  allocatedAmount: number;
  timesheetAmount: number;
}

export interface MonthlyProjectResult {
  id: string;
  name: string;
  color?: string | null;
  workingPeriod: WorkingPeriod;
  isFavorite: boolean;
  budget: Budget;
  performedAmount: number;
  budgetTracking: BudgetTracking;
  employeeBudgetAllocation?: EmployeeBudgetAllocation | null;
  teamMonthlyBudget?: TeamMonthlyBudget | null;
}

export interface TeamMonthlyBudget {
  allocatedAmount: number;
  timesheetAmount: number;
}

export enum BudgetTracking {
  MONTHLY = "monthly",
  GLOBAL = "global",
}

export enum PlannedTimeTracking {
  ORDER_AMOUNT = "order_amount",
  GLOBAL_BUDGET = "global_budget",
  INVOICED_AMOUNT = "invoiced_amount",
}

export interface LoggedHoursResult {
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
  loggedHours: number;
  plannedHours: number;
}

export interface ThemeResponse {
  theme: Omit<Theme, "project">;
}

export interface BudgetWithThemes extends Budget {
  themes: {
    __typename: "BudgetTheme";
    theme: Entity & { performedAmount: number };
    amount: number;
    invoicedAmount: number;
  }[];
}

export interface ThemeProjectWithBudget extends Entity {
  name: string;
  amount: number;
  performedAmount: number;
}

export interface ProjectWithBudgetAndThemes extends Project {
  budget: BudgetWithThemes;
  themes: ThemeProjectWithBudget[];
}

export enum ProjectResourcesType {
  URL = "url",
  EMBEDDED = "embedded",
}

export enum ProjectResourcesCategory {
  ANALYSIS = "analysis",
  REPORTING = "reporting",
}

export interface Expense extends Entity {
  id: string;
  name: string;
  date: Date;
  costNet: number;
  costGross: number;
  quantity: number;
  projectId: string;
  invoiceId: string;
  alreadyInvoiced: boolean;
}

export interface Offered extends Entity {
  id: string;
  date: Date;
  amountNet: number;
  comment: string;
  project: Project;
  invoice?: Invoice;
  creditNoteFor?: Invoice;
  validated: boolean;
}
